import filters from '@utils/filters'
import { RRule } from 'rrule'

class CalendarService

  @WEEKDAYS =
    [RRule.SU.weekday]: 'Sunday'
    [RRule.MO.weekday]: 'Monday'
    [RRule.TU.weekday]: 'Tuesday'
    [RRule.WE.weekday]: 'Wednesday'
    [RRule.TH.weekday]: 'Thursday'
    [RRule.FR.weekday]: 'Friday'
    [RRule.SA.weekday]: 'Saturday'

  @MAP_RRULE_TO_JS_DAY_OF_WEEK =
    'SU': 0
    'MO': 1
    'TU': 2
    'WE': 3
    'TH': 4
    'FR': 5
    'SA': 6
    '6': 0
    '0': 1
    '1': 2
    '2': 3
    '3': 4
    '4': 5
    '5': 6

  @MAP_JS_DAY_OF_WEEK_TO_RRULE =
    '0': RRule.SU.weekday
    '1': RRule.MO.weekday
    '2': RRule.TU.weekday
    '3': RRule.WE.weekday
    '4': RRule.TH.weekday
    '5': RRule.FR.weekday
    '6': RRule.SA.weekday

  @REVERSE_SHORT_WEEKDAYS =
    'Sunday' : 'SU'
    'Monday' : 'MO'
    'Tuesday' : 'TU'
    'Wednesday' : 'WE'
    'Thursday' : 'TH'
    'Friday' : 'FR'
    'Saturday' : 'SA'
  @SHORT_WEEKDAYS =
    'SU': 'Sunday'
    'MO': 'Monday'
    'TU': 'Tuesday'
    'WE': 'Wednesday'
    'TH': 'Thursday'
    'FR': 'Friday'
    'SA': 'Saturday'

  @MAP_RRULE_FREQ_TO_STRING =
    [RRule.YEARLY]: 'Annually'
    [RRule.MONTHLY]: 'Monthly'
    [RRule.WEEKLY]: 'Weekly'
    [RRule.DAILY]: 'Daily'

  @MAP_ORDINALITY =
    [-1]: 'Last'
    [1]: '1st'
    [2]: '2nd'
    [3]: '3rd'
    [4]: '4th'
    [5]: '5th'

  @DISPLAY_DATE_FORMAT = 'MM/DD/YYYY'
  @DATE_FORMAT = 'YYYY-MM-DD'

  @mixin: (options={}) ->
    methods:
      sync: (events) ->
        @actions.sync(JSON.parse(JSON.stringify(events)))

      findEvent: (id) ->
        @events.find((e) -> parseInt(e.id) is parseInt(id))

      removeInstanceEvent: (event, date) ->
        e = @findEvent event.id
        e.exdate ||= []
        e.exdate.push date
        @sync @events

      removeInstanceAndFollowingEvents: (event, date) ->
        event.rrule.until = dayjs(date).subtract(1, 'days').format(@DATE_FORMAT)
        @sync @events

      removeEvent: (event) ->
        events = @events.filter((e) -> parseInt(e.id) isnt parseInt(event.id))
        @sync events

      resizeRRuleEvent: (event, fromDates, toDates) ->
        newEvent = @duplicateEvent event.id, { start: toDates.start, end: toDates.end }
        @events.push newEvent

        event.exdate ||= []
        event.exdate.push fromDates.start
        newEvent

      resizeSingleEvent: (event, toDates) ->
        event.start = toDates.start
        event.end = toDates.end
        event

      moveRRuleInstance: (event, fromDates, toDates) ->
        newEvent = @duplicateEvent event.id, { start: toDates.start, end: toDates.end }
        @events.push newEvent

        event.exdate ||= []
        event.exdate.push fromDates.start
        newEvent

      moveSingleInstance: (event, toDates) ->
        e = @findEvent event.id
        e.start = toDates.start
        e.end = toDates.end

      duplicateEvent: (id, data) ->
        event = @findEvent id
        clone = JSON.parse(JSON.stringify(event))
        clone.id = Math.floor(Math.random() * 100)
        clone[key] = value for key, value of data
        clone.repeats = false
        delete clone.rrule
        delete clone.exrule
        delete clone.exdate
        clone

  @dayOptionsForEvents: (events) ->
    options = {}
    for event in events
      if event.repeats
        rrule = @rruleFromString(event.rrule_string)
        unless dayjs(event.starts_on).isSame(event.ends_on)
          days = for n in [0..dayjs(event.ends_on).diff(event.starts_on, 'day')]
            nextDate = dayjs(event.starts_on).add(n, 'days')
            if rrule.bysetpos
              @MAP_JS_DAY_OF_WEEK_TO_RRULE[parseInt(nextDate.format('d'))]
            else
              parseInt(nextDate.format('DD'))
          rrule.bymonthday = days
        options[option.value] = option for option in @generateRRuleOptions(rrule)
      else
        start = dayjs(event.starts_on)
        end = dayjs(event.ends_on)
        isSingleDay = end.isSame(start)
        if isSingleDay
          option = @generateSingleDayOption start
          options[option.value] = option
        else
          options[option.value] = option for option in @generateMultiDayOptions(start, end)

    options = Object.values(options)
    optionsByDate = options.filter((option) => option.bydate)
    optionsByMonthAndDay = options.filter((option) => option.bymonth)
    optionsByMonthDay = options.filter((option) => option.byday)
    optionsByLastDay = options.filter((option) => option.bylastday)
    optionsByWeekday = options.filter((option) => option.byweekday)

    [
      ...optionsByDate.sort((a, b) => a.sortValue.localeCompare(b.sortValue))
      ...optionsByMonthAndDay.sort((a, b) => a.sortValue.localeCompare(b.sortValue))
      ...optionsByMonthDay.sort((a, b) => a.sortValue -  b.sortValue)
      ...optionsByLastDay
      ...optionsByWeekday.sort((a, b) => a.sortValue - b.sortValue)
    ]

  @generateSingleDayOption: (start) ->
    label = start.format('MMM DD, YYYY')
    value = start.format(@DATE_FORMAT)
    sortValue = value
    { label, value, sortValue, bydate: true }

  @generateMultiDayOptions: (start, end) ->
    options = []
    numberOfDays = end.diff(start, 'day')
    for i in [0..numberOfDays]
      day = start.add(i, 'day')
      options.push @generateSingleDayOption(day)
    options

  @generateRRuleOptions: (rrule) ->
    options = []
    { freq, byweekday, bymonth, bymonthday, bysetpos } = rrule

    valueForByWeekDay = (weekday) => @WEEKDAYS[weekday].slice(0, 2).toUpperCase()

    if freq is RRule.WEEKLY
      for weekday in byweekday
        value =  valueForByWeekDay(weekday)
        sortValue = @MAP_RRULE_TO_JS_DAY_OF_WEEK[value]
        options.push { label: @WEEKDAYS[weekday], value, sortValue, byweekday: true }

    else if byweekday
      for weekday in byweekday
        value =  valueForByWeekDay(weekday)
        sortValue = @MAP_RRULE_TO_JS_DAY_OF_WEEK[value]
        options.push { label: @WEEKDAYS[byweekday], value, sortValue, byweekday: true }

    else if bymonthday
      isLastDayOfMonth = bymonthday.length is 4 and bysetpos?.includes -1

      if isLastDayOfMonth
        label = 'Last Day of Month'
        value = 'last_day_of_month'
        sortValue = value
        options.push { label, value, sortValue, bylastday: true }
      else
        month = bymonth
        for monthDay in bymonthday
          if bymonth
            month++ if monthDay < previousMonthDay
            month = 1 if month > 12
            label = "On day #{month}/#{monthDay}"
            value = "#{month}-#{monthDay}"
            sortValue = String(month).padStart(2, '0') + String(monthDay).padStart(2, '0')
            options.push { label, value, sortValue, bymonth: true }
          else if bysetpos
            label = @WEEKDAYS[monthDay]
            value = @REVERSE_SHORT_WEEKDAYS[label]
            sortValue = @MAP_RRULE_TO_JS_DAY_OF_WEEK[value]
            options.push { label, value, sortValue, byweekday: true }
          else
            label = "On day #{monthDay}"
            value = monthDay
            sortValue = value
            options.push { label, value, sortValue, byday: true }
          previousMonthDay = monthDay

    options

  @shortWeekdayForDate: (date) ->
    dayjs(date).format('dd').toUpperCase()

  @eventAvailabilities: (calendar, start, end) ->
    { events, seasons } = calendar
    availabilities = []

    for event in events
      # if {event.start} is after {start}, consider event.start

      if event.repeats
        dates = @activeDatesForRepeatingEvent event, start, end
        dates = dates.filter((date) => @isInSeason(seasons, date.start)) if seasons.length
        availabilities.push(...dates)
      else
        continue if dayjs(event.start).isBefore(start)
        eventEnd = event.end
        eventEnd = end if dayjs(event.end).isAfter(dayjs(end))
        dayOfWeek = @rruleDayOfWeekForJsDate(event.start)
        availabilities.push { start: dayjs(event.start).format(@DATE_FORMAT), end: eventEnd, day_of_week: dayOfWeek }

    availabilities

  @dateAvailability: (eventAvailabilities) ->
    availabilites = []

    for availability in eventAvailabilities
      { start, end } = availability

      if dayjs(start).isSame(end)
        availabilites.push availability
      else
        date = start
        while dayjs(date).isSameOrBefore(end)
          dayOfWeek = @rruleDayOfWeekForJsDate(date)
          availabilites.push { start: date, end: date, day_of_week: dayOfWeek }
          date = dayjs(date).add(1, 'day')

    availabilites

  @activeDatesForRepeatingEvent: (event, start, end) ->
    e = JSON.parse(JSON.stringify(event))

    dtstart = dayjs(e.rrule.dtstart).format(@DATE_FORMAT)
    e.rrule.dtstart = @chopUTCDate dtstart

    beginDate = dayjs(start)
    beginDate = dayjs() if dayjs(beginDate).isBefore(dayjs())
    activeStart = @chopUTCDate beginDate
    activeEnd = @chopUTCDate end

    rrule = new RRule e.rrule
    startDates = rrule.between(activeStart, activeEnd)
    duration = dayjs(e.start).diff(e.end, 'day')

    dates = []
    for startDate in startDates
      utcStartDate = startDate.toISOString().slice(0, 10)
      endDate = dayjs(utcStartDate).add(duration, 'day').format(@DATE_FORMAT)
      dayOfWeek = @rruleDayOfWeekForJsDate utcStartDate
      dates.push { start: dayjs(utcStartDate).format(@DATE_FORMAT), end: dayjs(endDate).format(@DATE_FORMAT), day_of_week: dayOfWeek }
    dates

  @maxReachForAvailabilityWindows: (availabilityWindows, listing, segment) ->
    { spaces } = listing
    maxReach = 0
    for av in availabilityWindows
      samplingCapacity = listing.daily_sampling_capacity[segment]

      if samplingCapacity
        if samplingCapacity[av.start]
          maxReach += parseInt(samplingCapacity[av.start])
        else if samplingCapacity[av.day_of_week]
          maxReach += parseInt(samplingCapacity[av.day_of_week])
    maxReach

  @minReachForAudienceListing: (listing) ->
    0

  @totalDailySamplingCapacity: (listing, segment, dateAvailability) ->
    dailySamplingCapacity = listing.daily_sampling_capacity[segment]
    total = 0
    for date in dateAvailability
      dayOfWeek = CalendarService.rruleDayOfWeekForJsDate date.start
      capacity = dailySamplingCapacity[date.start]
      capacity ||= dailySamplingCapacity[dayOfWeek]
      total += parseInt(capacity)
    total

  @campaignStartDate: (target) ->
    { type, count, period, date } = target.date.starts
    if type is 'rolling'
      value = dayjs().add(count, period)
    else
      value = dayjs(date)
    value.format('YYYY-MM-DD')

  @campaignEndDate: (target) ->
    { every_day, count, period } = target.date.duration
    { date } = target.date.ends
    if every_day
      value = dayjs(date)
    else
      value = dayjs(@campaignStartDate(target)).add(count, period)
    value.format('YYYY-MM-DD')

  @isInSeason: (seasons, date) ->
    day = dayjs(date)
    seasons.some((season) ->
      isTwoYearSeason = season.start_month > season.end_month
      inSeason = false

      if isTwoYearSeason
        firstHalfSeasonStart = "#{day.year()}-#{season.start_month}-#{season.start_day}"
        firstHalfSeasonEnd = "#{day.year()}-12-31"
        secondHalfSeasonStart = "#{day.year()}-01-01"
        secondHalfSeasonEnd = "#{day.year()}-#{season.end_month}-#{season.end_day}"
        isBetweenFirstHalf = day.isBetween firstHalfSeasonStart, firstHalfSeasonEnd, 'day', '[]'
        isBetweenSecondHalf = day.isBetween secondHalfSeasonStart, secondHalfSeasonEnd, 'day', '[]'
        inSeason = isBetweenFirstHalf || isBetweenSecondHalf
      else
        seasonStart = dayjs "#{day.year()}-#{season.start_month}-#{season.start_day}"
        seasonEnd = dayjs "#{day.year()}-#{season.end_month}-#{season.end_day}"
        inSeason = day.isBetween seasonStart, seasonEnd, 'day', '[]'

      inSeason
    )

  @earliestSeason: (seasons) ->
    return null unless seasons.length
    seasons.reduce((last, next) ->
      return last unless next?.starts_at
      return next unless last?.starts_at
      return last if dayjs(last.starts_at).isBefore(next.starts_at)
      next
    )

  @latestSeason: (seasons) ->
    return null unless seasons.length
    seasons.reduce((last, next) ->
      return last unless next?.ends_at
      return next unless last?.ends_at
      return last if dayjs(last.ends_at).isAfter(next.ends_at)
      next
    )

  @chopUTCDate: (date) ->
    strDate = dayjs(date).format(@DATE_FORMAT)
    dayjs.utc(strDate).toDate()

  @betweenDates: (start, end) ->
    dates = []
    currentDate = dayjs start
    while currentDate.isSameOrBefore(dayjs(end))
      dates.push dayjs(currentDate).format(@DATE_FORMAT)
      currentDate = dayjs(currentDate).add(1, 'day')
    dates

  @rruleDayOfWeekForJsDate: (date) ->
    idx = dayjs(date).format('d')
    @MAP_JS_DAY_OF_WEEK_TO_RRULE[idx]

  @jsDayOfWeekForRRule: (day) ->
    idx = String(day)
    @MAP_RRULE_TO_JS_DAY_OF_WEEK[idx]

  @normalizeEvents: (calendarEvents) ->
    JSON.parse(JSON.stringify(calendarEvents)).map((event) =>
      event.rrule = @rruleFromString event.rrule if event.rrule
      event
    )

  @rruleToString: (rrule) ->
    rule = new RRule { ...rrule }
    rule.toString().replace('RRULE:', '').replaceAll('\n', ';').replaceAll(':', '=')

  @rruleFromString: (rruleString) ->
    return rruleString if typeof rruleString is 'object'
    parsedRRule = RRule.fromString(rruleString)
    keys = Object.keys parsedRRule.origOptions
    rule = {}
    rule[key] = parsedRRule.options[key] for key in keys
    rule

  @formatCalendarDate: ({ date, repeats, rruleString, seasons = [] }) ->
    seasonalIcon = '<i class="fas fa-sun fa-fw"></i>'
    repeatsIcon = '<i class="fas fa-redo-alt fa-fw"></i>'
    start = dayjs date.substr(0, 10) if date
    start ||= dayjs()
    today = dayjs()
    str = ''

    if seasons.length > 0
      str += seasonalIcon

    if start.year() isnt today.year()
      str += "'#{start.format('YY')}: "

    if !repeats
      str += start.format('MMM D')
      return str

    rrule = CalendarService.rruleFromString rruleString
    { freq, byweekday, bymonthday, bysetpos } = rrule
    ordinality = @MAP_ORDINALITY[bysetpos]

    switch freq
      when RRule.YEARLY
        if ordinality
          str += "#{ordinality} #{start.format('ddd [of] MMM')} #{repeatsIcon} Annual"
        else
          str += "#{start.format('MMM D')} #{repeatsIcon} Annual"

      when RRule.MONTHLY
        isLastDayOfMonth = bymonthday?.length is 4 and bysetpos?.includes -1
        if isLastDayOfMonth
          str += "Last day #{repeatsIcon} Monthly"
        else if ordinality
          str += "#{ordinality} #{start.format('ddd')} #{repeatsIcon} Monthly"
        else
          str += "#{start.format('MMM D')} #{repeatsIcon} Monthly"

      when RRule.WEEKLY
        weekend = [RRule.SU.weekday, RRule.SA.weekday]
        weekdays = [RRule.MO.weekday, RRule.TU.weekday, RRule.WE.weekday, RRule.TH.weekday, RRule.FR.weekday]
        isEveryday = byweekday.length is 7
        isWeekend = byweekday.length is 2 and byweekday.sort().toString() is weekend.sort().toString()
        isWeekday = byweekday.length is 5 and byweekday.sort().toString() is weekdays.sort().toString()

        if isEveryday
          str += "#{repeatsIcon} Everyday"
        else if isWeekday
          str += "#{repeatsIcon} Every Weekday"
        else if isWeekend
          str += "#{repeatsIcon} Every Weekend"
        else if byweekday.length > 1
          weekLabels = byweekday.map((weekday) => @WEEKDAYS[weekday].slice(0, 3))
          weekLabels = "#{weekLabels.slice(0, -1).join(', ')} & #{weekLabels[weekLabels.length - 1]}"
          str += "#{repeatsIcon} Every #{weekLabels}"
        else if byweekday.length is 1
          weekdayLabel = @WEEKDAYS[byweekday[0]].slice(0, 3)
          str += "#{repeatsIcon} Every #{weekdayLabel}"
    str

export default CalendarService
